import React from 'react';
import { FilterGroup as FilterGroupType, FilterCondition, FilterPriority } from './types/filters';
import { CategorizedColumnSelect } from './CategorizedColumnSelect';

// Pseudo-column that matches across every profile field (like the search bar).
const ANY_FIELD_COL = 'any_field';

interface FilterGroupProps {
  group: FilterGroupType;
  availableColumns: Array<{ key: string; label: string; type: string }>;
  onUpdateGroup: (groupId: string, updates: Partial<FilterGroupType>) => void;
  onRemoveGroup: (groupId: string) => void;
  onAddCondition: (groupId: string) => void;
  onUpdateCondition: (groupId: string, conditionId: string, updates: Partial<FilterCondition>) => void;
  onRemoveCondition: (groupId: string, conditionId: string) => void;
  showPriorityToggle?: boolean;  // Show priority toggle for job spec matching mode
}

export const FilterGroupComponent: React.FC<FilterGroupProps> = ({
  group,
  availableColumns,
  onUpdateGroup,
  onRemoveGroup,
  onAddCondition,
  onUpdateCondition,
  onRemoveCondition,
  showPriorityToggle = false
}) => {
  // Toggle priority between required and preferred
  const togglePriority = () => {
    const newPriority: FilterPriority = group.priority === 'required' ? 'preferred' : 'required';
    onUpdateGroup(group.id, { priority: newPriority });
  };

  // Get border color based on priority
  const getBorderClass = () => {
    if (!group.priority) return 'border-gray-300';
    return group.priority === 'required' ? 'border-red-400' : 'border-green-400';
  };
  const getOperatorsForColumn = (columnKey: string) => {
    const column = availableColumns.find(c => c.key === columnKey);
    
    if (column?.type === 'number') {
      return [
        { value: 'equals', label: 'Equals' },
        { value: 'not_equals', label: 'Does not equal' },
        { value: 'greater_than', label: 'Greater than' },
        { value: 'less_than', label: 'Less than' },
        { value: 'greater_equal', label: 'Greater or equal' },
        { value: 'less_equal', label: 'Less or equal' },
        { value: 'is_empty', label: 'Is empty' },
        { value: 'is_not_empty', label: 'Is not empty' }
      ];
    }

    if (column?.type === 'boolean') {
      return [
        { value: 'is_true', label: 'Yes' },
        { value: 'is_false', label: 'No / never' }
      ];
    }

    if (column?.type === 'date') {
      return [
        { value: 'equals', label: 'On' },
        { value: 'greater_than', label: 'After' },
        { value: 'less_than', label: 'Before' },
        { value: 'greater_equal', label: 'On or after' },
        { value: 'less_equal', label: 'On or before' },
        { value: 'not_equals', label: 'Not on' },
        { value: 'is_empty', label: 'Is empty' },
        { value: 'is_not_empty', label: 'Is not empty' }
      ];
    }

    return [
      { value: 'contains', label: 'Contains' },
      { value: 'not_contains', label: 'Does not contain' },
      { value: 'equals', label: 'Equals' },
      { value: 'not_equals', label: 'Does not equal' },
      { value: 'starts_with', label: 'Starts with' },
      { value: 'ends_with', label: 'Ends with' },
      { value: 'is_empty', label: 'Is empty (blank)' },
      { value: 'is_not_empty', label: 'Is not empty' }
    ];
  };

  return (
    <div className={`mb-4 p-4 border-2 ${getBorderClass()} rounded-lg bg-white`}>
      {/* Group Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          {/* Priority Toggle - only show when in job spec matching mode */}
          {showPriorityToggle && group.priority && (
            <button
              onClick={togglePriority}
              className={`px-2 py-1 text-xs font-medium rounded flex items-center gap-1 ${
                group.priority === 'required'
                  ? 'bg-red-100 text-red-700 border border-red-300 hover:bg-red-200'
                  : 'bg-green-100 text-green-700 border border-green-300 hover:bg-green-200'
              }`}
              title={group.priority === 'required' ? 'Click to make Nice to Have' : 'Click to make Must Have'}
            >
              {group.priority === 'required' ? '🔴 Must Have' : '🟢 Nice to Have'}
            </button>
          )}

          <input
            type="text"
            value={group.name || ''}
            onChange={(e) => onUpdateGroup(group.id, { name: e.target.value })}
            placeholder="Group name (optional)"
            className="px-2 py-1 text-sm font-medium border-b border-transparent hover:border-gray-300 focus:border-blue-500 focus:outline-none"
          />

          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-600">Logic:</span>
            <button
              data-tour="filter-logic"
              onClick={() => onUpdateGroup(group.id, { logic: group.logic === 'AND' ? 'OR' : 'AND' })}
              className={`px-3 py-1 text-xs font-medium rounded ${
                group.logic === 'OR'
                  ? 'bg-purple-100 text-purple-700 border border-purple-300'
                  : 'bg-blue-100 text-blue-700 border border-blue-300'
              }`}
            >
              {group.logic}
            </button>
          </div>

          <span className="text-xs text-gray-500">
            {group.entityType === 'candidate' ? '👤 Candidate' : '🏢 Company'}
          </span>
        </div>

        <button
          onClick={() => onRemoveGroup(group.id)}
          className="text-xs text-red-600 hover:text-red-800 font-medium"
        >
          Remove Group
        </button>
      </div>

      {/* Conditions */}
      <div className="space-y-2">
        {group.conditions.map((condition, index) => (
          <div key={condition.id}>
            {index > 0 && (
              <div className="text-center my-1">
                <span className={`text-xs font-bold px-2 py-0.5 rounded ${
                  group.logic === 'OR' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                }`}>
                  {group.logic}
                </span>
              </div>
            )}
            
            <div data-tour="filter-condition" className="flex items-center gap-2 p-3 bg-gray-50 rounded border border-gray-200">
              {/* Column Select - Categorized */}
              <CategorizedColumnSelect
                columns={availableColumns}
                selectedColumn={condition.column}
                onSelectColumn={(columnKey) => {
                  // Reset the operator to a valid one for the new column type when
                  // the current operator no longer applies. Without this, picking a
                  // boolean column left operator='contains' while the dropdown showed
                  // 'Yes' — so selecting the already-shown 'Yes' fired no change and
                  // the filter stayed `contains ""` (matched everything). Preserves
                  // the operator when it's still valid (e.g. text → text).
                  const validOps = getOperatorsForColumn(columnKey).map(o => o.value);
                  const patch: Partial<FilterCondition> = { column: columnKey };
                  if (!validOps.includes(condition.operator)) {
                    patch.operator = validOps[0] as FilterCondition['operator'];
                  }
                  onUpdateCondition(group.id, condition.id, patch);
                }}
                placeholder="Select field..."
              />

              {condition.column === 'language_min_proficiency' ? (
                <>
                  {/* Language + minimum proficiency (correctly paired) */}
                  <input
                    type="text"
                    value={condition.value}
                    onChange={(e) => onUpdateCondition(group.id, condition.id, { value: e.target.value })}
                    placeholder="Language (e.g. German) — blank = any"
                    className="flex-1 px-2 py-1.5 text-xs border rounded"
                  />
                  <span className="text-xs text-gray-500 whitespace-nowrap">at least</span>
                  <select
                    value={condition.minProficiency || ''}
                    onChange={(e) => onUpdateCondition(group.id, condition.id, { minProficiency: e.target.value })}
                    className="px-2 py-1.5 text-xs border rounded"
                  >
                    <option value="">Any proficiency</option>
                    <option value="ELEMENTARY">Elementary</option>
                    <option value="LIMITED_WORKING">Limited working</option>
                    <option value="PROFESSIONAL_WORKING">Professional working</option>
                    <option value="FULL_PROFESSIONAL">Full professional</option>
                    <option value="NATIVE_OR_BILINGUAL">Native or bilingual</option>
                  </select>
                </>
              ) : condition.column === 'role_in_last_years' ? (
                <>
                  {/* Any role (title/keyword) held within the last N years */}
                  <input
                    type="text"
                    value={condition.value}
                    onChange={(e) => onUpdateCondition(group.id, condition.id, { value: e.target.value })}
                    placeholder="Job title or keyword — blank = any role"
                    className="flex-1 px-2 py-1.5 text-xs border rounded"
                  />
                  <span className="text-xs text-gray-500 whitespace-nowrap">in the last</span>
                  <select
                    value={condition.withinYears ?? 5}
                    onChange={(e) => onUpdateCondition(group.id, condition.id, { withinYears: parseInt(e.target.value, 10) })}
                    className="px-2 py-1.5 text-xs border rounded"
                  >
                    {[1, 2, 3, 4, 5, 7, 10, 15].map(y => (
                      <option key={y} value={y}>{y} {y === 1 ? 'year' : 'years'}</option>
                    ))}
                  </select>
                </>
              ) : (
                <>
                  {/* Operator Select */}
                  <select
                    value={condition.operator}
                    onChange={(e) => onUpdateCondition(group.id, condition.id, { operator: e.target.value as any })}
                    className="px-2 py-1.5 text-xs border rounded"
                  >
                    {getOperatorsForColumn(condition.column).map(op => (
                      <option key={op.value} value={op.value}>{op.label}</option>
                    ))}
                  </select>

                  {/* Value Input — date columns get a native date picker so the
                      value is always sent as ISO (YYYY-MM-DD); the backend then
                      compares on the calendar date. */}
                  {!['is_empty', 'is_not_empty', 'is_true', 'is_false'].includes(condition.operator) && (
                    availableColumns.find(c => c.key === condition.column)?.type === 'date' ? (
                      <input
                        type="date"
                        value={condition.value || ''}
                        onChange={(e) => onUpdateCondition(group.id, condition.id, { value: e.target.value })}
                        className="flex-1 px-2 py-1.5 text-xs border rounded"
                      />
                    ) : (
                      <input
                        type="text"
                        value={condition.values ? condition.values.join(' + ') : condition.value}
                        onChange={(e) => onUpdateCondition(
                          group.id,
                          condition.id,
                          // For "All columns", the backend re-parses the raw
                          // string into OR phrases, so clear any prior `values`
                          // array (which would otherwise take precedence and
                          // ignore the edit).
                          condition.column === ANY_FIELD_COL
                            ? { value: e.target.value, values: undefined }
                            : { value: e.target.value }
                        )}
                        placeholder={condition.column === ANY_FIELD_COL
                          ? 'e.g. "data center" OR "mission critical"'
                          : /years|tenure/.test(condition.column)
                            // Tenure is in years and takes decimals: counted live
                            // from the role start date (utils/tenure.ts).
                            ? 'Years — e.g. 0.25 = 3 months, 0.5 = 6 months'
                            : 'Value...'}
                        className="flex-1 px-2 py-1.5 text-xs border rounded"
                      />
                    )
                  )}
                </>
              )}

              {/* Remove Condition */}
              <button
                onClick={() => onRemoveCondition(group.id, condition.id)}
                className="text-red-600 hover:text-red-800 text-xs px-2"
              >
                ✕
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Add Condition Button */}
      <button
        data-tour="filter-add-condition"
        onClick={() => onAddCondition(group.id)}
        className="mt-3 w-full px-3 py-2 text-xs border-2 border-dashed border-gray-300 rounded hover:border-blue-500 hover:bg-blue-50 text-gray-600 hover:text-blue-600"
      >
        + Add Condition to Group
      </button>

      {/* Helper Text */}
      {group.conditions.length > 1 && (
        <div className="mt-2 text-xs text-gray-500 italic">
          💡 This group matches items where {group.logic === 'OR' ? 'ANY' : 'ALL'} of the above conditions are true
        </div>
      )}
    </div>
  );
};
